import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';

interface PopoutWindowProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Initial size as fraction of viewport (0-1). Default: 0.6 */
  initialScale?: number;
  children: React.ReactNode;
  /** Header actions rendered to the right of the title */
  headerActions?: React.ReactNode;
}

const PopoutWindow: React.FC<PopoutWindowProps> = ({
  isOpen,
  onClose,
  title,
  initialScale = 0.6,
  children,
  headerActions,
}) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  const initW = Math.round(vw * initialScale);
  const initH = Math.round(vh * initialScale);
  const initX = Math.round((vw - initW) / 2);
  const initY = Math.round((vh - initH) / 2);

  const [isMaximized, setIsMaximized] = useState(false);
  const [prevBounds, setPrevBounds] = useState({ x: initX, y: initY, width: initW, height: initH });
  const [bounds, setBounds] = useState({ x: initX, y: initY, width: initW, height: initH });

  const toggleMaximize = useCallback(() => {
    if (isMaximized) {
      setBounds(prevBounds);
      setIsMaximized(false);
    } else {
      setPrevBounds(bounds);
      setBounds({ x: 0, y: 0, width: vw, height: vh });
      setIsMaximized(true);
    }
  }, [isMaximized, bounds, prevBounds, vw, vh]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      <Rnd
        size={{ width: bounds.width, height: bounds.height }}
        position={{ x: bounds.x, y: bounds.y }}
        onDragStop={(_e, d) => {
          setBounds(prev => ({ ...prev, x: d.x, y: d.y }));
          if (isMaximized) setIsMaximized(false);
        }}
        onResizeStop={(_e, _dir, ref, _delta, position) => {
          setBounds({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
            x: position.x,
            y: position.y,
          });
          if (isMaximized) setIsMaximized(false);
        }}
        dragHandleClassName="popout-drag-handle"
        minWidth={320}
        minHeight={240}
        bounds="window"
        className="pointer-events-auto"
        enableResizing={!isMaximized}
      >
        <div className="w-full h-full bg-surface rounded-lg shadow-2xl border border-neutral-300 flex flex-col overflow-hidden">
          {/* Title bar — drag handle */}
          <div className="popout-drag-handle flex items-center justify-between px-3 py-2 bg-neutral-100 border-b border-neutral-200 cursor-move select-none flex-shrink-0">
            <span className="text-sm font-medium text-neutral-800 truncate">{title || 'Viewer'}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              {headerActions}
              {/* Maximize / Restore */}
              <button
                onClick={toggleMaximize}
                className="p-1 rounded hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700"
                title={isMaximized ? 'Restore' : 'Maximize'}
              >
                {isMaximized ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
              {/* Close */}
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-danger-100 text-neutral-500 hover:text-danger-600"
                title="Close"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {children}
          </div>
        </div>
      </Rnd>
    </div>,
    document.body,
  );
};

export default PopoutWindow;
